import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/apiServices';
 

const initialState = {
  roles: [],
  currentRole: null,
  loading: false,
  error: null,
  selectedRole: null,
};

export const fetchRoles = createAsyncThunk(
  'role/fetchRoles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get('role');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch roles');
    }
  }
);

export const getRoleById = createAsyncThunk(
    'role/getRoleById',
    async (id, { rejectWithValue }) => {
      try {
        const response = await apiService.get(`role/${id}`);
        return response.data.data;
      } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch role');
      }
    }
  );

export const createRole = createAsyncThunk(
  'role/createRole',
  async (roleData, { rejectWithValue }) => {
    try {
      const response = await apiService.post('role', roleData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create role');
    }
  }
);

export const updateRole = createAsyncThunk(
  'role/updateRole',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.put(`role/${id}`, data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update role');
    }
  }
);

export const deleteRole = createAsyncThunk(
  'role/deleteRole',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.delete(`role/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete role');
    }
  }
);

export const addPermissions = createAsyncThunk(
  'role/addPermissions',
  async ({ roleId, permissions }, { rejectWithValue }) => {
    try {
      const response = await apiService.post(`role/${roleId}/permissions`, { permissions });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add permissions');
    }
  }
);

export const removePermissions = createAsyncThunk(
  'role/removePermissions',
  async ({ roleId, permissions }, { rejectWithValue }) => {
    try {
      const response = await apiService.delete(`role/${roleId}/permissions`, { 
        data: { permissions } 
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove permissions');
    }
  }
);

const roleSlice = createSlice({
  name: 'role',
  initialState,
  reducers: {
    setCurrentRole: (state, action) => {
      state.currentRole = action.payload;
    },
    clearSelectedRole: (state) => {
        state.selectedRole = null;
      },
      clearRoleError: (state) => {
        state.error = null;
      }
  },
  extraReducers: (builder) => {
    builder
    .addCase(getRoleById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRoleById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedRole = action.payload;
        // Update role in roles array if exists
        const index = state.roles.findIndex(role => role._id === action.payload._id);
        if (index !== -1) {
          state.roles[index] = action.payload;
        }
      })
      .addCase(getRoleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Roles
      .addCase(fetchRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = action.payload;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Role
      .addCase(createRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.loading = false;
        state.roles.push(action.payload);
      })
      .addCase(createRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Role
      .addCase(updateRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = state.roles.map(role =>
          role._id === action.payload._id ? action.payload : role
        );
        if (state.currentRole?._id === action.payload._id) {
          state.currentRole = action.payload;
        }
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Role
      .addCase(deleteRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = state.roles.filter(role => role._id !== action.payload);
        if (state.currentRole?._id === action.payload) {
          state.currentRole = null;
        }
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add/Remove Permissions
      .addCase(addPermissions.fulfilled, (state, action) => {
        const updatedRole = action.payload;
        state.roles = state.roles.map(role =>
          role._id === updatedRole._id ? updatedRole : role
        );
        if (state.currentRole?._id === updatedRole._id) {
          state.currentRole = updatedRole;
        }
      })
      .addCase(removePermissions.fulfilled, (state, action) => {
        const updatedRole = action.payload;
        state.roles = state.roles.map(role =>
          role._id === updatedRole._id ? updatedRole : role
        );
        if (state.currentRole?._id === updatedRole._id) {
          state.currentRole = updatedRole;
        }
      });
  }
});

export const { clearSelectedRole,setCurrentRole, clearRoleError } = roleSlice.actions;
const roleReducer = roleSlice.reducer;

export default roleReducer;


// Selectors
export const selectSelectedRole = (state) => state.role.selectedRole;
export const selectRoleLoading = (state) => state.role.loading;
export const selectRoleError = (state) => state.role.error;
export const selectRoles = (state) => state.role.roles;